import { useState, useEffect, useMemo } from 'react';
import { Link } from '@tanstack/react-router';
import { motion, AnimatePresence } from 'motion/react';
import type {
  CollectionItemTreeNode,
  FlattenedArticleItem,
} from '@/types/article-collection';
import ChevronRightIcon from '@/assets/common/chevron-right.svg?react';
import FileTextIcon from '@/assets/common/file-text.svg?react';
import FolderTreeIcon from '@/assets/common/folder-tree.svg?react';
import styles from './CollectionReadingSidebar.module.scss';

interface CollectionReadingSidebarProps {
  tree: CollectionItemTreeNode[];
  flattenedArticles: FlattenedArticleItem[];
  currentArticleId: string;
  rootCollectionId: string;
}

// Get all heading IDs that are ancestors of the current article
function getAncestorHeadingIds(
  nodes: CollectionItemTreeNode[],
  targetArticleId: string,
  parentIds: string[] = []
): string[] {
  for (const node of nodes) {
    if (node.item_type === 'article' && node.article_id === targetArticleId) {
      return parentIds;
    }
    if (node.item_type === 'heading' && node.children.length > 0) {
      const found = getAncestorHeadingIds(
        node.children,
        targetArticleId,
        [...parentIds, node.id]
      );
      if (found.length > parentIds.length) {
        return found;
      }
    }
  }
  return [];
}

// Get position info for progress display
function getArticlePositionInfo(
  flattenedArticles: FlattenedArticleItem[],
  currentArticleId: string
): { current: number; total: number } | null {
  const index = flattenedArticles.findIndex(
    (a) => a.article_id === currentArticleId
  );
  if (index === -1) return null;
  return { current: index + 1, total: flattenedArticles.length };
}

export function CollectionReadingSidebar({
  tree,
  flattenedArticles,
  currentArticleId,
  rootCollectionId,
}: CollectionReadingSidebarProps) {
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());
  const [isMobileOpen, setIsMobileOpen] = useState(false);

  // Auto-expand ancestors of current article
  useEffect(() => {
    const ancestors = getAncestorHeadingIds(tree, currentArticleId);
    if (ancestors.length > 0) {
      setExpandedIds((prev) => {
        const next = new Set(prev);
        ancestors.forEach((id) => next.add(id));
        return next;
      });
    }
  }, [tree, currentArticleId]);

  // Close mobile drawer when article changes
  useEffect(() => {
    setIsMobileOpen(false);
  }, [currentArticleId]);

  const positionInfo = useMemo(
    () => getArticlePositionInfo(flattenedArticles, currentArticleId),
    [flattenedArticles, currentArticleId]
  );

  const handleToggleExpand = (id: string) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const collectionName = flattenedArticles[0]?.collection_name || 'Collection';

  const sidebarContent = (
    <div className={styles.sidebar}>
      <div className={styles.sidebar__header}>
        <h3 className={styles.sidebar__title}>{collectionName}</h3>
        {positionInfo && (
          <p className={styles.sidebar__progress}>
            Article {positionInfo.current} of {positionInfo.total}
          </p>
        )}
      </div>
      <div className={styles.sidebar__content}>
        <ul className={styles.sidebar__tree}>
          {tree.map((node) => (
            <TreeNode
              key={node.id}
              node={node}
              expandedIds={expandedIds}
              currentArticleId={currentArticleId}
              rootCollectionId={rootCollectionId}
              onToggleExpand={handleToggleExpand}
              depth={0}
            />
          ))}
        </ul>
      </div>
    </div>
  );

  return (
    <>
      {/* Desktop sidebar - rendered directly, parent handles visibility */}
      {sidebarContent}

      {/* Mobile toggle button */}
      <button
        className={styles.toggleButton}
        onClick={() => setIsMobileOpen(true)}
        aria-label="Open article list"
      >
        <FolderTreeIcon />
      </button>

      {/* Mobile overlay */}
      <div
        className={`${styles.overlay} ${isMobileOpen ? styles['overlay--visible'] : ''}`}
        onClick={() => setIsMobileOpen(false)}
      />

      {/* Mobile drawer */}
      <div className={`${styles.drawer} ${isMobileOpen ? styles['drawer--open'] : ''}`}>
        {sidebarContent}
      </div>
    </>
  );
}

interface TreeNodeProps {
  node: CollectionItemTreeNode;
  expandedIds: Set<string>;
  currentArticleId: string;
  rootCollectionId: string;
  onToggleExpand: (id: string) => void;
  depth: number;
}

function TreeNode({
  node,
  expandedIds,
  currentArticleId,
  rootCollectionId,
  onToggleExpand,
  depth,
}: TreeNodeProps) {
  const isHeading = node.item_type === 'heading';
  const isArticle = node.item_type === 'article';
  const isExpanded = expandedIds.has(node.id);
  const hasChildren = node.children.length > 0;

  const handleExpandClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    onToggleExpand(node.id);
  };

  // Article item
  if (isArticle && node.article_id) {
    return (
      <li>
        <Link
          to="/articles/collections/$collectionId/articles/$articleId"
          params={{
            collectionId: rootCollectionId,
            articleId: node.article_id,
          }}
          className={`${styles.articleItem} ${
            node.article_id === currentArticleId ? styles['articleItem--current'] : ''
          }`}
          style={{ paddingLeft: `${depth * 0.75 + 0.5}rem` }}
        >
          <span className={styles.articleItem__icon}>
            <FileTextIcon />
          </span>
          <span className={styles.articleItem__title}>
            {node.article_title || 'Untitled'}
          </span>
          {node.time_to_read_minutes && (
            <span className={styles.articleItem__duration}>
              {node.time_to_read_minutes}m
            </span>
          )}
        </Link>
      </li>
    );
  }

  // Heading item
  if (isHeading) {
    return (
      <li className={styles.headingNode}>
        <div
          className={styles.headingNode__header}
          style={{ paddingLeft: `${depth * 0.75}rem` }}
          onClick={handleExpandClick}
        >
          <button
            className={`${styles.headingNode__expandButton} ${
              isExpanded ? styles['headingNode__expandButton--expanded'] : ''
            } ${!hasChildren ? styles['headingNode__expandButton--hidden'] : ''}`}
            onClick={handleExpandClick}
            tabIndex={-1}
          >
            <ChevronRightIcon />
          </button>
          <span className={styles.headingNode__name}>{node.title}</span>
          {hasChildren && (
            <span className={styles.headingNode__count}>{node.children.length}</span>
          )}
        </div>

        <AnimatePresence>
          {isExpanded && hasChildren && (
            <motion.ul
              className={styles.headingNode__children}
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.2 }}
            >
              {node.children.map((child) => (
                <TreeNode
                  key={child.id}
                  node={child}
                  expandedIds={expandedIds}
                  currentArticleId={currentArticleId}
                  rootCollectionId={rootCollectionId}
                  onToggleExpand={onToggleExpand}
                  depth={depth + 1}
                />
              ))}
            </motion.ul>
          )}
        </AnimatePresence>
      </li>
    );
  }

  return null;
}
